import Attribution from "../../../geo/Attribution";
import React, { useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, Card, Row, Col, Spin } from "antd";
import { MapCanvas as GoogleMap, RouteLayer as DirectionsRenderer, useMapLoader } from "../../../geo/Map";
import {useGeoLookup} from "../../../geo/useGeoLookup";
import GeoRetry from "../../../geo/GeoRetry";
import { formatLocationAddress } from "../../../geo/addressLabel";
import {
  ClockCircleOutlined,
  LineOutlined,
  EnvironmentOutlined,
  SwapRightOutlined,
} from "@ant-design/icons";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: true,
};

function MapDialog({ open, onClose, origin, destination, traslado }) {
  const {isLoaded,loadError,version}=useMapLoader();
  const routeLookup=useGeoLookup('route',origin && destination?{origin,destination}:null,{enabled:open && isLoaded,version});
  const result=routeLookup.data;
  const startLookup=useGeoLookup('reverse',origin,{enabled:open && isLoaded && !!result && !result.start_address,version});
  const endLookup=useGeoLookup('reverse',destination,{enabled:open && isLoaded && !!result && !result.end_address,version});
  const startAddress=result?.start_address || formatLocationAddress(startLookup.data?.result);
  const endAddress=result?.end_address || formatLocationAddress(endLookup.data?.result);
  const directionsResponse=useMemo(()=>result?{...result,start_address:startAddress,end_address:endAddress}:null,[result,startAddress,endAddress]);
  const loading=routeLookup.busy;
  const routeInfo={distance:result?`${(result.distance_m/1000).toFixed(1)} km`:loading?'Calculando...':'No disponible',
    duration:result?`${Math.ceil(result.duration_s/60)} min`:loading?'Calculando...':'No disponible',
    startAddress:startAddress || (startLookup.busy?'Buscando dirección...':'No disponible'),
    endAddress:endAddress || (endLookup.busy?'Buscando dirección...':'No disponible'),
    startAttribution:startLookup.data?.result?.attribution,endAttribution:endLookup.data?.result?.attribution};

  const mapRef = useRef(null);

  const directionsRendererOptions = useMemo(() => ({
    suppressMarkers: false,
    preserveViewport: false, // Solo ajustar viewport la primera vez
    polylineOptions: {
      strokeColor: "#1890ff",
      strokeOpacity: 0.8,
      strokeWeight: 5,
    },
  }), []);

  // Centro inicial del mapa - solo se calcula una vez
  const initialCenter = useMemo(() => {
    if (origin && destination) {
      return {
        lat: (origin.lat + destination.lat) / 2,
        lng: (origin.lng + destination.lng) / 2,
      };
    }
    return { lat: 4.6097, lng: -74.0817 }; // Bogota default
  }, [origin, destination]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  if (!isLoaded) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title="Ruta en Mapa"
        width={850}
        centered
      >
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <p style={{ marginTop: 20 }}>{loadError?.message || "Cargando mapa..."}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <EnvironmentOutlined />
          <span>Ruta del Traslado</span>
        </div>
      }
      width={900}
      centered
      styles={{
        body: {
          padding: "24px",
          backgroundColor: "#f4f4f4",
        },
      }}
    >
      {/* Info de origen/destino del traslado */}
      {traslado && (
        <Card
          size="small"
          style={{ marginBottom: 16, backgroundColor: "#fff" }}
        >
          <Row gutter={16} align="middle">
            <Col xs={24} sm={11}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <EnvironmentOutlined
                  style={{ color: "#52c41a", fontSize: 18, marginTop: 2 }}
                />
                <div>
                  <div style={{ fontWeight: 600, color: "#52c41a", fontSize: 12 }}>
                    ORIGEN
                  </div>
                  <div style={{ fontWeight: 500 }}>
                    {traslado.origen_ciudad || "N/A"},{" "}
                    {traslado.origen_departamento || ""}
                  </div>
                  {traslado.origen_direccion && (
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {traslado.origen_direccion}
                    </div>
                  )}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={2} style={{ textAlign: "center" }}>
              <SwapRightOutlined style={{ fontSize: 20, color: "#1890ff" }} />
            </Col>
            <Col xs={24} sm={11}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <EnvironmentOutlined
                  style={{ color: "#ff4d4f", fontSize: 18, marginTop: 2 }}
                />
                <div>
                  <div style={{ fontWeight: 600, color: "#ff4d4f", fontSize: 12 }}>
                    DESTINO
                  </div>
                  <div style={{ fontWeight: 500 }}>
                    {traslado.destino_ciudad || "N/A"},{" "}
                    {traslado.destino_departamento || ""}
                  </div>
                  {traslado.destino_direccion && (
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {traslado.destino_direccion}
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <GeoRetry lookup={routeLookup} label="Reintentar ruta" />
      <GeoRetry lookup={startLookup} label="Reintentar dirección de inicio" />
      <GeoRetry lookup={endLookup} label="Reintentar dirección de fin" />
      {/* Mapa */}
      <div style={{ position: "relative" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "8px",
            }}
          >
            <Spin size="large" tip="Calculando ruta..." />
          </div>
        )}
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={initialCenter}
          zoom={12}
          options={mapOptions}
          onLoad={onMapLoad}
        >
          {directionsResponse && (
            <DirectionsRenderer
              directions={directionsResponse}
              options={directionsRendererOptions}
            />
          )}
        </GoogleMap>
          <Attribution attribution={directionsResponse?.attribution} />
          <Attribution attribution={routeInfo.startAttribution} />
          <Attribution attribution={routeInfo.endAttribution} />
      </div>

      {/* Estadisticas */}
      <div style={{ marginTop: "24px" }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card
              style={{
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
            >
              <ClockCircleOutlined
                style={{ fontSize: "24px", color: "#faad14" }}
              />
              <div style={{ fontWeight: 600, marginTop: "8px", fontSize: 12, color: "#666" }}>
                Tiempo estimado
              </div>
              <div style={{ fontSize: "16px", fontWeight: 500, color: "#333" }}>
                {routeInfo.duration}
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={6}>
            <Card
              style={{
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
            >
              <LineOutlined style={{ fontSize: "24px", color: "#52c41a" }} />
              <div style={{ fontWeight: 600, marginTop: "8px", fontSize: 12, color: "#666" }}>
                Distancia
              </div>
              <div style={{ fontSize: "16px", fontWeight: 500, color: "#333" }}>
                {routeInfo.distance}
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12}>
            <Card
              style={{
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Row gutter={8}>
                <Col span={12}>
                  <EnvironmentOutlined
                    style={{ fontSize: "16px", color: "#52c41a" }}
                  />
                  <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                    Dirección origen
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#333",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }}
                    title={routeInfo.startAddress}
                  >
                    {routeInfo.startAddress || "Calculando..."}
                  </div>
                </Col>
                <Col span={12}>
                  <EnvironmentOutlined
                    style={{ fontSize: "16px", color: "#ff4d4f" }}
                  />
                  <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                    Dirección destino
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#333",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }}
                    title={routeInfo.endAddress}
                  >
                    {routeInfo.endAddress || "Calculando..."}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    </Modal>
  );
}

MapDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  origin: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  destination: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  traslado: PropTypes.object,
};

export default MapDialog;
